/**
 * SettingsDropdown — Header preferences panel
 * Extracted from App.js as part of AppRouter refactor.
 * Language section now wired to ManchalaI18n (18 locales).
 */
import React, { useEffect, useRef } from 'react';
import { Type, Languages, Coins, Globe, PencilRuler } from 'lucide-react';
import { CURRENCIES } from '@/constants';
import { useI18n } from '../../lib/manchala-i18n.react.js';

/** Returns true for any role that can edit UI translations */
function canTranslateUI(userRole) {
  const role = (userRole || '').toLowerCase();
  if (role === 'super_admin' || role === 'licensor' || role === 'adminsupport') return true;
  // Fallback: check adminFeatures in storage for scoped admins
  try {
    const ctx = JSON.parse(
      localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context') || '{}'
    );
    return (Array.isArray(ctx?.adminFeatures) ? ctx.adminFeatures : []).includes('DYNAMIC_ATTRIBUTES');
  } catch { return false; }
}

const SettingsDropdown = ({
  isOpen, onClose, theme,
  fontSize, setFontSize,
  currency, setCurrency,
  userRole,
  // legacy language props kept for API compat but i18n provider is used directly
}) => {
  const isDark = theme === 'dark';
  const ref = useRef(null);
  const { lang, setLang, locales } = useI18n();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showTranslateBtn = canTranslateUI(userRole);

  const openTranslationEditor = () => {
    onClose();
    // Dispatch the same keyboard shortcut the I18nTranslationEditor already listens for
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'L', ctrlKey: true, shiftKey: true, bubbles: true, cancelable: true })
    );
  };

  return (
    <div ref={ref} data-testid="settings-dropdown" data-dropdown-panel
      className={`dropdown-panel absolute right-0 top-full mt-2 w-72 border rounded-xl shadow-2xl z-[200] overflow-hidden ${isDark ? 'bg-[#1a1a1a] border-[#333]' : ''}`}>
      <div className={`px-4 py-3 border-b ${isDark ? 'border-[#333] bg-[#151515]' : 'border-slate-100 bg-slate-50'}`}>
        <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Preferences</p>
      </div>
      <div className="p-3 space-y-3">
        {/* Font Size */}
        <div>
          <label className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Type size={12} /> Font Size
          </label>
          <div className={`flex p-0.5 rounded-lg ${isDark ? 'bg-[#111]' : 'bg-slate-100'}`}>
            {['small', 'medium', 'large'].map((s) => (
              <button key={s} data-testid={`font-size-${s}`} onClick={() => setFontSize(s)}
                className={`flex-1 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all ${
                  fontSize === s
                    ? 'bg-blue-600 text-white shadow'
                    : isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                }`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Language — wired to ManchalaI18n (18 locales) */}
        <div>
          <label className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Globe size={12} /> Language
          </label>
          <select
            data-testid="language-select"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-xs font-medium outline-none ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
          >
            {locales.length > 0
              ? locales.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))
              : <option value="en-IN">English</option>
            }
          </select>
          <p className={`text-[9px] mt-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            Selected: {lang} &bull; ERP screens display in English until translated
          </p>
        </div>

        {/* Currency */}
        <div>
          <label className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Coins size={12} /> Country / Currency
          </label>
          <select data-testid="currency-select" value={currency} onChange={(e) => setCurrency(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-xs font-medium outline-none ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Translate UI — visible only to admin roles */}
        {showTranslateBtn && (
          <div className={`pt-1 border-t ${isDark ? 'border-[#2a2a2a]' : 'border-slate-100'}`}>
            <label className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <PencilRuler size={12} /> Admin Tools
            </label>
            <button
              data-testid="translate-ui-btn"
              onClick={openTranslationEditor}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all
                ${isDark
                  ? 'bg-[#111] border-[#333] text-violet-400 hover:bg-violet-900/20 hover:border-violet-500/40'
                  : 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100 hover:border-violet-400'
                }`}
            >
              <Languages size={14} />
              Translate UI Labels
              <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded border ${isDark ? 'bg-[#222] border-[#333] text-slate-500' : 'bg-white border-slate-200 text-slate-400'}`}>
                Ctrl+Shift+L
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsDropdown;
